/**
 * Utility functions for message formatting in chat components.
 */

/**
 * OpenAI multimodal content part types
 */
export interface TextContentPart {
  type: "text";
  text: string;
}

export interface ImageContentPart {
  type: "image_url";
  image_url: { url: string };
}

export type MultimodalContentPart = TextContentPart | ImageContentPart;

/**
 * Message group returned by groupMessagesByTime
 */
export interface MessageGroup<T> {
  messages: T[];
  time: string;
}

/**
 * Formats a timestamp into a human-readable time string.
 */
function formatGroupTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  
  if (isToday) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString([], { month: "short", day: "numeric" }) + 
    " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * Groups consecutive messages by time proximity and role.
 * Messages from the same role within TIME_THRESHOLD_MS are grouped together.
 *
 * @param messages - Array of messages to group (must have id, timestamp, and optional role)
 * @returns Array of message groups with time labels
 */
export function groupMessagesByTime<T extends { id: string; timestamp: number; role?: string }>(
  messages: T[]
): MessageGroup<T>[] {
  if (messages.length === 0) return [];

  const TIME_THRESHOLD_MS = 60000; // 1 minute threshold for grouping
  const groups: MessageGroup<T>[] = [];
  let currentGroup: T[] = [messages[0]];

  for (let i = 1; i < messages.length; i++) {
    const prevMessage = messages[i - 1];
    const currMessage = messages[i];
    const timeDiff = currMessage.timestamp - prevMessage.timestamp;
    const sameRole = prevMessage.role === currMessage.role;

    // Start a new group if role changes or time gap exceeds threshold
    if (!sameRole || timeDiff > TIME_THRESHOLD_MS) {
      const lastMessage = currentGroup[currentGroup.length - 1];
      groups.push({ 
        messages: currentGroup,
        time: formatGroupTime(lastMessage.timestamp)
      });
      currentGroup = [currMessage];
    } else {
      currentGroup.push(currMessage);
    }
  }

  // Push the last group
  if (currentGroup.length > 0) {
    const lastMessage = currentGroup[currentGroup.length - 1];
    groups.push({ 
      messages: currentGroup,
      time: formatGroupTime(lastMessage.timestamp)
    });
  }

  return groups;
}

/**
 * Formats message content for OpenAI multimodal format.
 *
 * If no images are provided, returns the text as-is.
 * If images are provided, returns an array of content parts
 * in the OpenAI multimodal format.
 *
 * @param text - The text content of the message
 * @param images - Array of base64 image data URLs
 * @returns The formatted content (string or array of content parts)
 */
export function formatMessageContent(
  text: string,
  images: string[]
): string | MultimodalContentPart[] {
  if (images.length === 0) {
    return text;
  }

  const content: MultimodalContentPart[] = [];

  // Add text part first
  if (text.trim()) {
    content.push({ type: "text", text: text });
  }

  // Add image parts
  images.forEach((imageDataUrl) => {
    content.push({
      type: "image_url",
      image_url: { url: imageDataUrl },
    });
  });

  return content;
}
